import type React from 'react';
import type {DoppeDtoActionViewType} from '../../client-server-common/types/doppe-dto-action';
import type {IDoppeViewerActionContext} from '../doppe-viewer-action-context/doppe-viewer-action-context';
import type {AnyObject, HtmlDataAttributes, ObservableValue, OmitStrict, HideableValue } from '@wix/devzai-utils-common';
import type {Button, Icon} from '@wix/devzai-utils-react';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {BymoPageStyleScope} from '../../client-server-common';

export type DoppeActionComponentActionButtonProps = OmitStrict<Button.Props, 'tagName'> & {
    /**
     * Optional trailing icon rendered on the right of the action button by the
     * action-button template (see `DoppeStandardActionButtonTemplate`). Used by
     * actions whose button represents a specific terminal interaction — e.g.
     * `DoppeActionTypeDownloadFile` surfaces a download glyph — so the affordance
     * is recognizable without reading the title.
     *
     * The template renders this icon only when the action is NOT locked and not
     * a widget-opener (the chevron / lock icons take precedence in those modes).
     */
    suffixIcon?: Icon.Src;
};


export interface DoppeActionView {

    showContent: () => void;

    showActionButton () : void;
}

export namespace DoppeActionView {

    export interface InlineContentCloseButtonRenderProps {
        closingFunction: () => void;
    }

    export interface TemplateContextProps {
        renderInlineContentCloseButton: (renderProps: InlineContentCloseButtonRenderProps) => React.ReactElement | null;
        actionWidgetFramelessContainerClassName?: string;
    }

    /**
     * Properties which value are provided to the action component in a context of a specific action.
     * These properties cannot be configured by the user.
     */
    export interface ActionContextProps {
        label: string;
        actionDescription: HideableValue<string>;
        actionImage: HideableValue<WixMediaResource | null>;
        actionIndexInList: number | null;
        bannerMedia: WixMediaResource | null;
        viewType: DoppeDtoActionViewType;
        showActionHeader: boolean;
        renderActionWidget: (htmlAttributes: React.HTMLAttributes<any>) => React.ReactElement;
        actionButtonComponentProps: DoppeActionComponentActionButtonProps;
        activateCTA: () => void;
        isWidgetOpener: boolean;
        isLockedObservable?: ObservableValue<boolean>;
        skipReportingOnGoalAchievedOnActionButtonClick: boolean;
        actionContext: IDoppeViewerActionContext<any>;
        isExpanded: boolean;
        actionViewRootHtmlAttributes: React.HTMLAttributes<any> & HtmlDataAttributes;
    }

    export interface ContextProps extends ActionContextProps, TemplateContextProps {

    }

    export interface Props<TEMPLATE_PROPS extends {} = Record<string, any>> extends ContextProps {
        templateProps: TEMPLATE_PROPS;
        enableAnimation: boolean;
        forcedBymoPageStyleScope?: BymoPageStyleScope;
    }
}

export interface DoppeActionButtonTemplateMetadata<PARAMS extends AnyObject> {
    initialValues: PARAMS;
}



export interface DoppeActionButtonTemplateDefinition<PARAMS extends AnyObject> extends DoppeActionButtonTemplateMetadata<PARAMS> {
    componentType: React.ComponentType<DoppeActionView.Props<PARAMS>>;
}

export function doppeActionButtonTemplateDefine<PARAMS extends AnyObject> (
    metadata: DoppeActionButtonTemplateMetadata<PARAMS>,
    componentType: React.ComponentType<DoppeActionView.Props<PARAMS>>
) : DoppeActionButtonTemplateDefinition<PARAMS> {
    return {
        ...metadata,
        componentType: componentType
    };
}

export function doppeActionButtonTemplateCreateMetadata<PARAMS extends AnyObject> (
    metadata: DoppeActionButtonTemplateMetadata<PARAMS>
) {
    return metadata;
}

export function dopeActionButtonTemplateResolveProps<PARAMS extends AnyObject> (
    actionButtonTemplateMetadata: DoppeActionButtonTemplateMetadata<PARAMS>,
    partialProps: Partial<PARAMS>
) : PARAMS {
    return {
        ...actionButtonTemplateMetadata.initialValues,
        ...partialProps
    }
}